import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useLocation } from "react-router-dom";

type Lang = "en" | "tr" | "de";

interface LanguageContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

const translations: Record<string, Record<Lang, string>> = {
  // Navbar
  "nav.home": { en: "Home", tr: "Ana Sayfa", de: "Startseite" },
  "nav.about": { en: "About", tr: "Hakkımızda", de: "Über uns" },
  "nav.services": { en: "Services", tr: "Hizmetler", de: "Leistungen" },
  "nav.portfolio": { en: "Portfolio", tr: "Portfolyo", de: "Portfolio" },
  "nav.news": { en: "News", tr: "Haberler", de: "News" },
  "nav.contact": { en: "Contact", tr: "İletişim", de: "Kontakt" },
  "nav.get_quote": { en: "Get a Quote", tr: "Teklif Alın", de: "Angebot anfordern" },

  // Footer
  "footer.navigation": { en: "Navigation", tr: "Navigasyon", de: "Navigation" },
  "footer.rights": { en: "All rights reserved.", tr: "Tüm hakları saklıdır.", de: "Alle Rechte vorbehalten." },
  "footer.privacy": { en: "Privacy Policy", tr: "Gizlilik Politikası", de: "Datenschutz" },

  // HomePage
  "home.since": { en: "Since 1989 — Istanbul & Budapest", tr: "1989'dan beri — İstanbul & Budapeşte", de: "Seit 1989 — Istanbul & Budapest" },
  "home.hero_headline_default": { en: "We Don't Just Organize Events. We Turn Your Vision Into an Unforgettable Experience.", tr: "Biz Sadece Etkinlik Düzenlemiyoruz. Vizyonunuzu Unutulmaz Bir Deneyime Dönüştürüyoruz.", de: "Wir organisieren nicht nur Events. Wir verwandeln Ihre Vision in ein unvergessliches Erlebnis." },
  "home.hero_sub_default": { en: "From Istanbul to Budapest, Dubai to Prague,\n35 years of flawless technical production and event management\nacross 15+ countries.", tr: "İstanbul'dan Budapeşte'ye, Dubai'den Prag'a,\n35 yılı aşkın kusursuz teknik prodüksiyon ve etkinlik yönetimi\n15+ ülkede.", de: "Von Istanbul bis Budapest, von Dubai bis Prag,\n35 Jahre makellose technische Produktion und Eventmanagement\nin über 15 Ländern." },
  "home.view_our_work": { en: "View Our Work", tr: "Çalışmalarımız", de: "Unsere Arbeiten" },
  "home.what_we_do": { en: "What We Do", tr: "Ne Yapıyoruz", de: "Was wir tun" },
  "home.full_service": { en: "Full-Service Event Production", tr: "Tam Hizmet Etkinlik Prodüksiyonu", de: "Full-Service Eventproduktion" },
  "home.full_service_sub": { en: "From concept to curtain call, CSG handles every layer of your event.", tr: "Konseptten final perdesine kadar, CSG etkinliğinizin her aşamasını yönetir.", de: "Vom Konzept bis zum Schlussvorhang betreut CSG jede Ebene Ihres Events." },
  "home.featured_work": { en: "Featured Work", tr: "Öne Çıkan Çalışmalar", de: "Ausgewählte Arbeiten" },
  "home.selected_projects": { en: "Selected Projects", tr: "Seçilmiş Projeler", de: "Ausgewählte Projekte" },
  "home.view_all_projects": { en: "View All Projects", tr: "Tüm Projeleri Gör", de: "Alle Projekte ansehen" },
  "home.testimonials": { en: "Testimonials", tr: "Referanslar", de: "Referenzen" },
  "home.what_clients_say": { en: "What Our Clients Say", tr: "Müşterilerimiz Ne Diyor", de: "Was unsere Kunden sagen" },
  "home.click_read_more": { en: "Click to read more →", tr: "Devamını oku →", de: "Weiterlesen →" },
  "home.our_offices": { en: "Our Offices", tr: "Ofislerimiz", de: "Unsere Büros" },
  "home.two_cities": { en: "Two Cities, One Vision", tr: "İki Şehir, Bir Vizyon", de: "Zwei Städte, eine Vision" },
  "home.ready": { en: "Ready?", tr: "Hazır mısınız?", de: "Bereit?" },
  "home.cta_default": { en: "Ready to Create Something Exceptional?", tr: "Olağanüstü Bir Şey Yaratmaya Hazır mısınız?", de: "Bereit, etwas Außergewöhnliches zu schaffen?" },
  "home.cta_sub": { en: "Tell us about your event and let's bring your vision to life.", tr: "Etkinliğiniz hakkında bize bilgi verin, vizyonunuzu hayata geçirelim.", de: "Erzählen Sie uns von Ihrem Event und wir erwecken Ihre Vision zum Leben." },
  "home.get_in_touch": { en: "Get in Touch", tr: "İletişime Geçin", de: "Kontakt aufnehmen" },

  // Services Page
  "services.our_services": { en: "Our Services", tr: "Hizmetlerimiz", de: "Unsere Leistungen" },
  "services.hero_title_1": { en: "Full-Service Event", tr: "Tam Hizmet Etkinlik", de: "Full-Service Event" },
  "services.hero_title_2": { en: "Production", tr: "Prodüksiyonu", de: "Produktion" },
  "services.hero_desc": { en: "From concept to curtain call, CSG handles every layer of your event. AV, staging, lighting, simultaneous interpretation, delegate systems, decor, and stand fabrication. Whatever your event demands, we make it work beautifully.", tr: "Konseptten final perdesine kadar, CSG etkinliğinizin her katmanını yönetir. AV, sahne, aydınlatma, simultane tercüme, delege sistemleri, dekorasyon ve stant üretimi. Etkinliğiniz ne gerektiriyorsa, biz onu mükemmel hale getiriyoruz.", de: "Vom Konzept bis zum Schlussvorhang betreut CSG jede Ebene Ihres Events. AV, Bühne, Beleuchtung, Simultandolmetschen, Delegiertensysteme, Dekoration und Standbau. Was auch immer Ihr Event erfordert – wir setzen es perfekt um." },
  "services.custom_solution": { en: "Need a Custom Solution?", tr: "Özel Bir Çözüme mi İhtiyacınız Var?", de: "Brauchen Sie eine maßgeschneiderte Lösung?" },
  "services.custom_solution_sub": { en: "Every event is unique. Let's discuss your specific requirements.", tr: "Her etkinlik benzersizdir. Özel gereksinimlerinizi konuşalım.", de: "Jedes Event ist einzigartig. Lassen Sie uns über Ihre Anforderungen sprechen." },

  // About Page
  "about.cta_default": { en: "Let's Work Together", tr: "Birlikte Çalışalım", de: "Lassen Sie uns zusammenarbeiten" },

  // Portfolio Page
  "portfolio.our_work": { en: "Our Work", tr: "Çalışmalarımız", de: "Unsere Arbeiten" },
  "portfolio.hero_title_1": { en: "500+ EVENTS", tr: "500+ ETKİNLİK", de: "500+ EVENTS" },
  "portfolio.hero_title_2": { en: "Zero Compromise", tr: "SIFIR TAVİZ", de: "Keine Kompromisse" },
  "portfolio.all": { en: "All", tr: "Tümü", de: "Alle" },
  "portfolio.no_projects": { en: "No projects in this category yet.", tr: "Bu kategoride henüz proje yok.", de: "Noch keine Projekte in dieser Kategorie." },
  "portfolio.have_project": { en: "Have a Project in Mind?", tr: "Aklınızda Bir Proje mi Var?", de: "Haben Sie ein Projekt im Kopf?" },

  // News Page
  "news.title": { en: "News & Blog", tr: "Haberler & Blog", de: "News & Blog" },
  "news.latest": { en: "Latest", tr: "Son", de: "Neueste" },
  "news.updates": { en: "Updates", tr: "Güncellemeler", de: "Updates" },
  "news.no_posts": { en: "No posts yet.", tr: "Henüz yazı yok.", de: "Noch keine Beiträge." },

  // Contact Page
  "contact.title": { en: "Contact", tr: "İletişim", de: "Kontakt" },
  "contact.hero_title_1": { en: "Ready to Create Something", tr: "Olağanüstü Bir Şey", de: "Bereit, etwas" },
  "contact.hero_title_2": { en: "Exceptional?", tr: "Yaratmaya Hazır mısınız?", de: "Außergewöhnliches zu schaffen?" },
  "contact.hero_sub": { en: "Let's talk about your next event.", tr: "Bir sonraki etkinliğiniz hakkında konuşalım.", de: "Sprechen wir über Ihr nächstes Event." },
  "contact.name": { en: "Name *", tr: "Ad Soyad *", de: "Name *" },
  "contact.name_placeholder": { en: "Your name", tr: "Adınız", de: "Ihr Name" },
  "contact.company": { en: "Company", tr: "Şirket", de: "Unternehmen" },
  "contact.company_placeholder": { en: "Company name", tr: "Şirket adı", de: "Firmenname" },
  "contact.email": { en: "Email *", tr: "E-posta *", de: "E-Mail *" },
  "contact.phone": { en: "Phone", tr: "Telefon", de: "Telefon" },
  "contact.event_type": { en: "Event Type", tr: "Etkinlik Türü", de: "Eventtyp" },
  "contact.select_type": { en: "Select type", tr: "Tür seçin", de: "Typ auswählen" },
  "contact.budget": { en: "Budget Range", tr: "Bütçe Aralığı", de: "Budgetrahmen" },
  "contact.select_range": { en: "Select range", tr: "Aralık seçin", de: "Bereich auswählen" },
  "contact.message": { en: "Message *", tr: "Mesaj *", de: "Nachricht *" },
  "contact.message_placeholder": { en: "Tell us about your event...", tr: "Etkinliğiniz hakkında bilgi verin...", de: "Erzählen Sie uns von Ihrem Event..." },
  "contact.sending": { en: "Sending...", tr: "Gönderiliyor...", de: "Wird gesendet..." },
  "contact.send": { en: "Send Message", tr: "Mesaj Gönder", de: "Nachricht senden" },
  "contact.success": { en: "Thank you! We'll get back to you within 24 hours.", tr: "Teşekkürler! 24 saat içinde size dönüş yapacağız.", de: "Vielen Dank! Wir melden uns innerhalb von 24 Stunden." },
  "contact.error": { en: "Something went wrong. Please try again.", tr: "Bir hata oluştu. Lütfen tekrar deneyin.", de: "Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut." },
  // Formun altındaki aydınlatma satırı. {link} yerine gizlilik sayfası
  // bağlantısı geçirilir, bkz. pages/ContactPage.tsx.
  "contact.privacy_notice": {
    en: "By sending this form you agree that we may process the details you provide in order to answer your enquiry, and that a hashed version of your email and phone number is shared with Google for advertising measurement. Details are in our {link}.",
    tr: "Formu göndererek, paylaştığınız bilgileri talebinizi yanıtlamak için işlememize ve e-posta ile telefon bilginizin şifrelenmiş bir karşılığının reklam ölçümü amacıyla Google ile paylaşılmasına onay vermiş olursunuz. Ayrıntılar {link} sayfamızda.",
    de: "Mit dem Absenden des Formulars stimmen Sie zu, dass wir Ihre Angaben zur Beantwortung Ihrer Anfrage verarbeiten und dass ein gehashter Wert Ihrer E-Mail-Adresse und Telefonnummer zur Werbemessung an Google übermittelt wird. Einzelheiten finden Sie in unserer {link}.",
  },
  "contact.privacy_link": { en: "Privacy Policy", tr: "Gizlilik Politikası", de: "Datenschutzerklärung" },
  "contact.istanbul_office": { en: "Istanbul Office", tr: "İstanbul Ofisi", de: "Büro Istanbul" },
  "contact.budapest_office": { en: "Budapest Office", tr: "Budapeşte Ofisi", de: "Büro Budapest" },
  "contact.corporate_conference": { en: "Corporate Conference", tr: "Kurumsal Konferans", de: "Firmenkonferenz" },
  "contact.medical_congress": { en: "Medical Congress", tr: "Tıbbi Kongre", de: "Medizinischer Kongress" },
  "contact.product_launch": { en: "Product Launch", tr: "Ürün Lansmanı", de: "Produkteinführung" },
  "contact.concert": { en: "Concert / Entertainment", tr: "Konser / Eğlence", de: "Konzert / Unterhaltung" },
  "contact.gala": { en: "Gala Dinner / Awards", tr: "Gala Yemeği / Ödül Töreni", de: "Gala-Dinner / Awards" },
  "contact.exhibition": { en: "Exhibition / Trade Show", tr: "Fuar / Sergi", de: "Messe / Ausstellung" },
  "contact.tech_summit": { en: "Technology Summit", tr: "Teknoloji Zirvesi", de: "Technologie-Gipfel" },
  "contact.hybrid": { en: "Hybrid / Virtual Event", tr: "Hibrit / Sanal Etkinlik", de: "Hybrid- / Virtuelles Event" },
  "contact.other": { en: "Other", tr: "Diğer", de: "Sonstiges" },

  // Blog Article
  "blog.all_articles": { en: "All Articles", tr: "Tüm Yazılar", de: "Alle Artikel" },
  "blog.not_found": { en: "Article not found", tr: "Makale bulunamadı", de: "Artikel nicht gefunden" },
  "blog.back": { en: "← Back to blog", tr: "← Blog'a dön", de: "← Zurück zum Blog" },
  "blog.by": { en: "By", tr: "Yazar:", de: "Von" },
  "blog.cta_title": { en: "Get Professional Support", tr: "Profesyonel Destek Alın", de: "Professionelle Unterstützung erhalten" },
  "blog.cta_sub": { en: "Elevate your event with CSG's expert team.", tr: "CSG'nin uzman ekibiyle etkinliğinizi bir üst seviyeye taşıyın.", de: "Bringen Sie Ihr Event mit dem Expertenteam von CSG auf ein neues Niveau." },

  // Service Detail
  "service.not_found": { en: "Service not found", tr: "Hizmet bulunamadı", de: "Leistung nicht gefunden" },
  "service.back": { en: "← Back to services", tr: "← Hizmetlere dön", de: "← Zurück zu den Leistungen" },
  "service.all_services": { en: "All Services", tr: "Tüm Hizmetler", de: "Alle Leistungen" },
  "service.capabilities": { en: "Technical Capabilities", tr: "Teknik Kapasiteler", de: "Technische Möglichkeiten" },
  "service.related_projects": { en: "Related Projects", tr: "İlgili Projeler", de: "Verwandte Projekte" },
  "service.related_services": { en: "Related Services", tr: "İlgili Hizmetler", de: "Verwandte Leistungen" },
  "service.cta_title": { en: "Let's Discuss Your Project", tr: "Projenizi Konuşalım", de: "Sprechen wir über Ihr Projekt" },
  "service.cta_sub": { en: "Get a custom quote for your event.", tr: "Etkinliğiniz için özel bir teklif alın.", de: "Erhalten Sie ein individuelles Angebot für Ihr Event." },
};

const SUPPORTED: Lang[] = ["en", "tr", "de"];
const isLang = (v: unknown): v is Lang => typeof v === "string" && (SUPPORTED as string[]).includes(v);

/**
 * URL'den dil tespiti.
 *
 * SEO açısından kritik: /tr/... ve /en/... yolları o dilin kanonik adresleridir.
 * Dil yalnızca localStorage/IP'den belirlenirse Googlebot (ABD IP) Türkçe URL'leri
 * İngilizce render eder; Google da TR sayfayı EN sayfanın kopyası sayıp dizine almaz.
 * Bu yüzden yol öneki her zaman kayıtlı tercihi ezer.
 */
const langFromPath = (pathname: string): Lang | null => {
  if (pathname === "/tr" || pathname.startsWith("/tr/")) return "tr";
  if (pathname === "/en" || pathname.startsWith("/en/")) return "en";
  return null;
};

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [lang, setLangState] = useState<Lang>(() => {
    const fromPath = typeof window !== "undefined" ? langFromPath(window.location.pathname) : null;
    if (fromPath) return fromPath;
    const saved = localStorage.getItem("csg_lang");
    return isLang(saved) ? saved : "en";
  });
  const [detected, setDetected] = useState(false);

  // Yol değiştiğinde dili yola göre sabitle. Kullanıcı dil değiştiricisini
  // kullanırsa yol aynı kaldığı için bu efekt yeniden tetiklenmez.
  useEffect(() => {
    const fromPath = langFromPath(pathname);
    if (fromPath) {
      setLangState(fromPath);
      setDetected(true);
    }
  }, [pathname]);

  useEffect(() => {
    if (langFromPath(window.location.pathname)) {
      // Dil URL'den belli; IP tespiti yapma ve kullanıcının kayıtlı tercihini bozma.
      setDetected(true);
      return;
    }
    const saved = localStorage.getItem("csg_lang");
    if (saved) {
      setDetected(true);
      return;
    }
    // Try IP-based geolocation first, fallback to browser language
    fetch("https://ipapi.co/json/", { signal: AbortSignal.timeout(3000) })
      .then(res => res.json())
      .then(data => {
        const country = data?.country_code;
        const detectedLang: Lang =
          country === "TR" ? "tr" :
          (country === "DE" || country === "AT" || country === "CH") ? "de" :
          "en";
        setLangState(detectedLang);
        localStorage.setItem("csg_lang", detectedLang);
        setDetected(true);
      })
      .catch(() => {
        // Fallback to browser language
        const browserLang = navigator.language || (navigator as any).userLanguage || "";
        const fallbackLang: Lang =
          browserLang.startsWith("tr") ? "tr" :
          browserLang.startsWith("de") ? "de" :
          "en";
        setLangState(fallbackLang);
        localStorage.setItem("csg_lang", fallbackLang);
        setDetected(true);
      });
  }, []);

  const setLang = (l: Lang) => {
    setLangState(l);
    localStorage.setItem("csg_lang", l);
  };

  const t = (key: string): string => {
    return translations[key]?.[lang] || translations[key]?.en || key;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}
